import React, {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import Button from "../../ui/button";
import {Action} from "../../types";
import WithTooltip from "../../ui/tooltip";
import {AddCircleOutlineIcon, AddIcon, CloseCircleOutlineIcon, HelpCircleIcon} from "../../ui/icons";
import Timeline from "./Timeline";
import classes from "./ChallengeActionsAddRow.module.css";
import IconButton from "../../ui/iconbutton";

type props = {
  className?: string;
  onAdd?: (type: Action["__typename"]) => void;
  disabled?: boolean;
  showHelpIcon?: boolean;
};

function ChallengeActionsAdd({
  onAdd,
  disabled,
  extras,
}: {
  onAdd?: props["onAdd"];
  disabled?: props["disabled"];
  extras?: React.ReactNode;
}) {
  const {t} = useTranslation();

  const handlers = useMemo(() => {
    if (!onAdd) {
      return {};
    }

    return {
      AvatarAction: () => onAdd("AvatarAction"),
      SystemAction: () => onAdd("SystemAction"),
      UserInputAction: () => onAdd("UserInputAction"),
      ChoiceUserInputAction: () => onAdd("ChoiceUserInputAction"),
    };
  }, [onAdd]);

  return (
    <div className={classes.row}>
      <Button className={classes.btn} onClick={handlers.AvatarAction} disabled={disabled}>
        <AddIcon className={classes.btnIcon} />
        {t("components.ChallengeActionsAddRow.AvatarAction")}
      </Button>

      <Button className={classes.btn} onClick={handlers.UserInputAction} disabled={disabled}>
        <AddIcon className={classes.btnIcon} />
        {t("components.ChallengeActionsAddRow.UserInputAction")}
      </Button>

      <Button className={classes.btn} onClick={handlers.SystemAction} disabled={disabled}>
        <AddIcon className={classes.btnIcon} />
        {t("components.ChallengeActionsAddRow.SystemAction")}
      </Button>

      <Button className={classes.btn} onClick={handlers.ChoiceUserInputAction} disabled={disabled}>
        <AddIcon className={classes.btnIcon} />
        {t("components.ChallengeActionsAddRow.ChoiceUserInputAction")}
      </Button>

      <div className={classes.extras}>{extras}</div>
    </div>
  );
}

export default function ChallengeActionsAddRow({className, onAdd, showHelpIcon, disabled}: props) {
  const {t} = useTranslation();

  return (
    <Timeline.Row className={className}>
      <ChallengeActionsAdd
        onAdd={onAdd}
        disabled={disabled}
        extras={
          showHelpIcon && (
            <WithTooltip className={classes.extraIcon} helpText={t("components.ChallengeActionsAddRow.helpText")}>
              <HelpCircleIcon fontSize={16} />
            </WithTooltip>
          )
        }
      />
    </Timeline.Row>
  );
}

ChallengeActionsAddRow.Collapsable = function ChallengeActionsAddRowCollapsable({disabled, onAdd}: props) {
  const [collapsed, setCollapsed] = useState(true);

  const show = useCallback(() => setCollapsed(false), []);
  const hide = useCallback(() => setCollapsed(true), []);

  return (
    <Timeline.Row narrow={collapsed} className={classes.collapsable} onMouseLeave={hide}>
      {collapsed ? (
        <button className={classes.placeholder} onClick={show}>
          <div className={classes.separator} />
          <span className={classes.collapseText}>
            <AddCircleOutlineIcon />
          </span>
          <div className={classes.separator} />
        </button>
      ) : (
        <ChallengeActionsAdd
          disabled={disabled}
          onAdd={onAdd}
          extras={
            <IconButton
              size="s"
              variant="outlined"
              className={classes.extraIcon}
              icon={CloseCircleOutlineIcon}
              onClick={hide}
            />
          }
        />
      )}
    </Timeline.Row>
  );
};
